import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Send, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo, getDiscordAvatarUrl, extractYouTubeId, getYouTubeThumbnail } from '../lib/timeUtils';
import { 
  fetchVideoDetail, 
  likeVideo, 
  fetchVideoComments, 
  createVideoComment, 
  deleteVideoComment,
  deleteVideo,
  fetchVideos
} from '../lib/api';
import ConfirmModal from '../components/ConfirmModal';

const VideoDetail = () => {
  const { videoType, videoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isAuthor, setIsAuthor] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const showModal = (config) => {
    setModalConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      onConfirm: config.onConfirm || null
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const loadVideo = async () => {
      if (hasLoaded) return;
      
      if (!videoId || !videoType) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const videoData = await fetchVideoDetail(videoType, videoId);
        setVideo(videoData);
        setIsLiked(videoData.is_liked || false);
        setLikesCount(videoData.likes_count || 0);
        
        if (user && videoData.author_discord_id === user.discord_id) {
          setIsAuthor(true);
        }
        setHasLoaded(true);
      } catch (err) {
        console.error('Erro ao carregar vídeo:', err);
        setError('Vídeo não encontrado');
      } finally {
        setLoading(false);
      }
    };

    loadVideo();
  }, [videoId, videoType, hasLoaded, user]);

  // Carregar vídeos relacionados
  useEffect(() => {
    const loadRelatedVideos = async () => {
      if (!videoType) return;
      
      try {
        const videos = await fetchVideos(videoType, 10);
        // Filtrar o vídeo atual e pegar os primeiros 5
        const filtered = (videos || []).filter(v => v.id !== videoId).slice(0, 5);
        setRelatedVideos(filtered);
      } catch (err) {
        console.error('Erro ao carregar vídeos relacionados:', err);
      }
    };

    if (video) {
      loadRelatedVideos();
    }
  }, [video, videoType, videoId]);

  // Carregar comentários
  useEffect(() => {
    const loadComments = async () => {
      if (!videoId || !videoType || loading) return;
      
      try {
        setCommentsLoading(true);
        const commentsData = await fetchVideoComments(videoType, videoId);
        setComments(commentsData || []);
      } catch (err) {
        console.error('Erro ao carregar comentários:', err);
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }
    };

    loadComments();
  }, [videoId, videoType, loading]);

  const handleLike = async () => {
    if (liking) return;
    
    try {
      setLiking(true);
      const newIsLiked = !isLiked;
      setIsLiked(newIsLiked);
      setLikesCount(prev => newIsLiked ? prev + 1 : Math.max(prev - 1, 0));
      
      const response = await likeVideo(videoType, videoId);
      setIsLiked(response.is_liked);
      setLikesCount(response.likes_count);
    } catch (err) {
      console.error('Erro ao curtir vídeo:', err);
      setIsLiked(!isLiked);
      setLikesCount(prev => isLiked ? prev + 1 : Math.max(prev - 1, 0));
    } finally {
      setLiking(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;
    
    try {
      setSubmittingComment(true);
      await createVideoComment(videoType, videoId, newComment);
      
      // Recarregar comentários
      const commentsData = await fetchVideoComments(videoType, videoId);
      setComments(commentsData || []);
      setNewComment('');
    } catch (err) {
      console.error('Erro ao criar comentário:', err);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao criar comentário. Tente novamente.'
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    showModal({
      type: 'confirm',
      title: 'Confirmar exclusão',
      message: 'Tem certeza que deseja excluir este comentário?',
      onConfirm: async () => {
        try {
          await deleteVideoComment(videoType, videoId, commentId);
          setComments(comments.filter(comment => comment.id !== commentId));
        } catch (err) {
          console.error('Erro ao deletar comentário:', err);
          showModal({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao deletar comentário. Tente novamente.'
          });
        }
      }
    });
  };

  const handleDeleteVideo = async () => {
    showModal({
      type: 'confirm',
      title: 'Confirmar exclusão',
      message: 'Tem certeza que deseja excluir este vídeo? Esta ação é irreversível.',
      onConfirm: async () => {
        try {
          await deleteVideo(videoType, videoId);
          navigate('/feed');
        } catch (err) {
          console.error('Erro ao deletar vídeo:', err);
          showModal({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao deletar vídeo. Tente novamente.'
          });
        }
      }
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-white">Carregando vídeo...</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error || 'Vídeo não encontrado'}</p>
          <Link
            to="/feed"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
          >
            Voltar ao Feed
          </Link>
        </div>
      </div>
    );
  }

  const videoEmbedUrl = `https://www.youtube.com/embed/${video.youtube_video_id}?autoplay=1`;
  const authorAvatar = getDiscordAvatarUrl(video.author_discord_id, video.author_avatar);

  // Filtrar vídeos relacionados
  let filteredRelatedVideos = relatedVideos;
  if (filterType === 'channel') {
    filteredRelatedVideos = relatedVideos.filter(v => v.author_id === video.author_id);
  }

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white" style={{ position: 'relative' }}>
      {/* Background Glow */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `radial-gradient(circle at 50% 0%, rgba(139, 92, 246, 0.15) 0%, transparent 60%)`
        }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#0a0a14]/95 backdrop-blur-[20px] border-b border-purple-500/20 px-8 py-3 flex justify-between items-center z-[1000]">
        <Link to="/feed" className="flex items-center gap-2.5 text-[22px] font-extrabold text-white no-underline">
          <img src="/oasis-logo.ico" alt="Oasis" className="w-8 h-8 rounded-lg" />
          <span>OASIS</span>
        </Link>
        <div className="bg-white/5 border border-purple-500/20 rounded-[20px] px-4 py-2 w-[400px] flex items-center text-gray-400">
          🔍 Buscar...
        </div>
        <Link to="/perfil" className="w-9 h-9 rounded-full cursor-pointer overflow-hidden">
          <img 
            src={getDiscordAvatarUrl(user?.discord_id, user?.discord_avatar)} 
            alt={user?.discord_username}
            className="w-full h-full object-cover"
          />
        </Link>
      </header>

      <div className="max-w-[1700px] mx-auto px-8 pt-20 pb-8 grid grid-cols-[1fr_400px] gap-8 relative z-[1]">
        
        {/* Left Column: Video & Info */}
        <main className="w-full">
          
          {/* Video Player */}
          <div className="w-full mb-5" style={{ aspectRatio: '16/9' }}>
            <div className="w-full h-full bg-black rounded-2xl overflow-hidden relative shadow-[0_0_40px_rgba(139,92,246,0.2)] border border-purple-500/10">
              <iframe
                src={videoEmbedUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={video.title || 'Video'}
              ></iframe>
            </div>
          </div>

          {/* Title & Actions */}
          <h1 className="text-2xl font-bold mb-2.5 leading-[1.3]">
            {video.title || 'Sem título'}
          </h1>
          
          <div className="flex justify-between items-center mb-5 pb-5 border-b border-white/10">
            <div className="text-gray-400 text-sm">
              {formatNumber(video.views_count || video.likes_count || 0)} visualizações • Estreou {formatTimeAgo(video.created_at)}
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={handleLike}
                disabled={liking}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-[20px] cursor-pointer text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isLiked 
                    ? 'bg-white text-[#0a0a14] border border-white/10' 
                    : 'bg-white/5 border border-white/10 text-white hover:bg-purple-500/15 hover:border-purple-500'
                }`}
              >
                👍 {formatNumber(likesCount)}
              </button>
              <button className="flex items-center gap-2 bg-white/5 border border-white/10 text-white px-5 py-2.5 rounded-[20px] cursor-pointer text-sm font-medium transition-all hover:bg-purple-500/15 hover:border-purple-500">
                🔗 Compartilhar
              </button>
            </div>
          </div>

          {/* Channel Info & Participants */}
          <div className="flex justify-between items-center mb-5 bg-[#111827]/40 px-5 py-4 rounded-xl border border-purple-500/10">
            <div className="flex items-center gap-4">
              <img 
                src={authorAvatar}
                alt={video.author_username}
                className="w-[50px] h-[50px] rounded-full border-2 border-purple-500 cursor-pointer"
                onClick={() => navigate(`/perfil/${video.author_id}`)}
              />
              <div>
                <h3 className="text-base">{video.author_username}</h3>
              </div>
            </div>

            {/* Participants */}
            {video.mentioned_users && Array.isArray(video.mentioned_users) && video.mentioned_users.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-[13px] text-gray-400">Com:</span>
                {video.mentioned_users.map((mentioned, idx) => {
                  const userDiscordId = mentioned.discord_id || mentioned;
                  const username = mentioned.username || userDiscordId;
                  const avatar = mentioned.avatar;
                  
                  return (
                    <div
                      key={idx}
                      onClick={() => navigate(`/perfil/${mentioned.id || userDiscordId}`)}
                      className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 px-3.5 py-1.5 rounded-[20px] cursor-pointer transition-all hover:bg-purple-500/25 hover:border-purple-500 hover:-translate-y-0.5"
                    >
                      <img
                        src={getDiscordAvatarUrl(userDiscordId, avatar)}
                        alt={username}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-[13px] font-medium text-white">{username}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Description */}
          {video.description && (
            <div className="bg-white/3 p-4 rounded-xl text-sm leading-relaxed text-gray-300 mb-10">
              <p>{video.description}</p>
            </div>
          )}

          {/* Comments */}
          <div>
            <div className="flex items-center gap-5 mb-6">
              <h3 className="text-lg font-semibold">{comments.length} Comentários</h3>
              <span className="text-sm text-gray-400 cursor-pointer">Ordenar por: Relevantes ▼</span>
            </div>

            {/* Comment Input */}
            <div className="flex gap-4 mb-8">
              <img 
                src={getDiscordAvatarUrl(user?.discord_id || '0', user?.discord_avatar)}
                alt="Me"
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitComment(e);
                    }
                  }}
                  placeholder="Adicione um comentário..."
                  className="w-full bg-transparent border-0 border-b-2 border-white/10 px-0 py-2.5 text-white text-[15px] outline-none transition-colors focus:border-purple-500"
                />
                {newComment.trim() && (
                  <div className="flex justify-end mt-2.5 gap-2.5">
                    <button
                      onClick={() => setNewComment('')}
                      className="bg-transparent border-0 text-white cursor-pointer px-4 py-2"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSubmitComment}
                      disabled={submittingComment}
                      className={`px-4 py-2 rounded-[20px] border-0 transition-all ${
                        submittingComment
                          ? 'bg-white/10 text-gray-400 cursor-not-allowed opacity-50'
                          : 'bg-purple-500 text-white cursor-pointer hover:bg-purple-600'
                      }`}
                    >
                      {submittingComment ? 'Enviando...' : 'Comentar'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Comments List */}
            {commentsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                Nenhum comentário ainda. Seja o primeiro a comentar!
              </div>
            ) : (
              <div className="space-y-6">
                {comments.map((comment) => {
                  const commentAvatar = getDiscordAvatarUrl(comment.user_discord_id || comment.author_discord_id, comment.author_avatar);
                  const isCommentAuthor = user && (comment.user_discord_id === user.discord_id || comment.author_discord_id === user.discord_id);
                  
                  return (
                    <div key={comment.id} className="flex gap-4">
                      <img 
                        src={commentAvatar}
                        alt={comment.username || comment.author_username}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h4 className="text-[13px] mb-1.5 flex gap-2 items-center">
                          <span className="font-semibold">{comment.username || comment.author_username}</span>
                          <span className="text-gray-500 font-normal text-xs">• {formatTimeAgo(comment.created_at)}</span>
                        </h4>
                        <p className="text-sm leading-relaxed text-gray-200 mb-2">{comment.content}</p>
                        <div className="flex gap-4 text-xs text-gray-400">
                          <button className="bg-transparent border-0 text-inherit cursor-pointer flex items-center gap-1.5 hover:text-white">
                            Responder
                          </button>
                          {isCommentAuthor && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="bg-transparent border-0 text-inherit cursor-pointer hover:text-red-400"
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* Right Column: Sidebar */}
        <aside className="w-full">
          <div className="flex gap-2.5 mb-5">
            <span 
              onClick={() => setFilterType('all')}
              className={`bg-white/10 px-3 py-1.5 rounded-[15px] text-[13px] cursor-pointer transition-all ${
                filterType === 'all' ? 'bg-white text-[#0a0a14]' : ''
              }`}
            >
              Todos
            </span>
            <span 
              onClick={() => setFilterType('channel')}
              className={`bg-white/10 px-3 py-1.5 rounded-[15px] text-[13px] cursor-pointer transition-all ${
                filterType === 'channel' ? 'bg-white text-[#0a0a14]' : ''
              }`}
            >
              Do canal {video.author_username}
            </span>
            <span className="bg-white/10 px-3 py-1.5 rounded-[15px] text-[13px] cursor-pointer transition-all">
              Relacionados
            </span>
          </div>

          <div className="space-y-4">
            {filteredRelatedVideos.map((relatedVideo) => {
              const relatedVideoId = relatedVideo.youtube_video_id || extractYouTubeId(relatedVideo.youtube_url);
              return (
                <div
                  key={relatedVideo.id}
                  onClick={() => navigate(`/video/${videoType}/${relatedVideo.id}`)}
                  className="flex gap-2.5 cursor-pointer transition-all hover:bg-white/5 rounded-lg p-2"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="w-[168px] h-[94px] rounded-lg overflow-hidden relative flex-shrink-0">
                    <img
                      src={getYouTubeThumbnail(relatedVideoId) || '/placeholder.jpg'}
                      alt="Thumb"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-[11px] px-1 py-0.5 rounded text-white">
                      {/* Duração não disponível */}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm leading-snug mb-1.5 line-clamp-2">
                      {relatedVideo.title || 'Sem título'}
                    </h4>
                    <div className="text-xs text-gray-400 leading-normal">
                      {relatedVideo.author_username}<br/>
                      {formatNumber(relatedVideo.views_count || relatedVideo.likes_count || 0)} visualizações • {formatTimeAgo(relatedVideo.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      {/* Modal de confirmação/erro */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default VideoDetail;
